import type { Config, DOMPurifyI } from 'dompurify';

import * as jsonpath from '../../../../../../External/jsonpath';
import { escape } from 'lodash';
import { shellStringResources } from '../../Resources/ShellStringResources';

const theDomPurifyGetter: () => DOMPurifyI = ()=> globalThis.DOMPurify;

export function format(template: string, ...args: string[]): string {
    return (args || []).reduce((prev, curr, index) => prev.replace(new RegExp(`\\{${index}\\}`, 'g'), curr), template);
}

// Use this if the args might contain content from customer input
export function formatWithEscape(template: string, ...args: string[]): string {
    args = args.map((a) => escape(a));
    return format(template, ...args);
}

export function getConnectorLegacyNameByFlag(connectorName: string, legacyFlag: boolean): string {
    return legacyFlag ? `${connectorName} ${shellStringResources.LegacyName}` : connectorName;
}

export function getConnectorV1Name(connectorName: string): string {
    return `${connectorName} V1`;
}

export function getConnectorV2Name(connectorName: string, isFabric: boolean = false): string {
    if (isFabric) {
        return connectorName;
    }
    return `${connectorName} V2`;
}

/**
 * Strictly sanitize a dirty string from any dom tag or attributes
 * @param dirty
 * @param args
 */
export function sanitizeAndFormatString(dirty: string, ...args: string[]): string{
    const theConfig: Config & { RETURN_DOM_FRAGMENT?: false | undefined; RETURN_DOM?: false | undefined } = {
        ALLOWED_TAGS: ['#text'],
        KEEP_CONTENT: true
    };
    const clean = theDomPurifyGetter().sanitize(dirty, theConfig);
    return format(clean, ...args);
}

/**
 * Sanitize and format the string with DOMPurify's sanitize option
 * @param dirty
 * @param option do not support return dom or dom fragement
 *
 * @return string
 */
export function sanitizeAndFormatStringWithOption(dirty: string, option: {
    args?: string[],
    config?: Config & { RETURN_DOM_FRAGMENT?: false | undefined; RETURN_DOM?: false | undefined },
} = {}): string{
    const theConfig: Config & { RETURN_DOM_FRAGMENT?: false | undefined; RETURN_DOM?: false | undefined } | undefined = option.config;
    const theArgs: string[] = option.args ?? [];
    if (theConfig){
        const clean = theDomPurifyGetter().sanitize(dirty, theConfig);
        return format(clean, ...theArgs);
    }
    return sanitizeAndFormatString(dirty, ...theArgs);
}

export function escapeForHtml(text: string): string {
    return text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#x27;')
                .replace(/\//g, '&#x2F;');
}

export function stringsAreEqualCaseInsensitive(string1?: string, string2?: string): boolean {
    if (string1 == null && string2 == null) {
        return true;
    }
    if (string1 == null || string2 == null) {
        return false;
    }
    return string1.toLowerCase() === string2.toLowerCase();
}

export function stringIsNullOrEmpty(value?: string): value is null | undefined | '' {
    return value == null || value === '';
}

export function valueIsNullOrEmpty(value?: unknown): value is null | undefined | '' {
    return value == null || value === '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isString(obj: any): boolean {
    return typeof obj === 'string';
}

export function parseJsonPath(path: string): jsonpath.PathComponent[] {
    try {
        let relativePathWorkAround = false;
        // path with [] as start is relative path, which is not parsable by the lib. Work around with full path
        if (path.startsWith('[')) {
            relativePathWorkAround = true;
            path = '$' + path;
        }
        const result = jsonpath.parse(path).map((p) => p.expression.value);
        if (relativePathWorkAround) {
            result.shift();
        }
        return result;
    } catch (e) {
        return undefined;
    }
}

/*
 * If string is null or undefined, return empty string ''
 * If string starts with an alphabet character, capitalize it and return the string
 * If string starts with a none alphabet character, return it as it
 */
export function capitalize(text: string): string {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertKeyToUpperCamelCase(obj: Record<string, any>): Record<string, any> {
    const upperConverter = (key: string): string => {
        return key.replace(/^\S/g, (x) => x.toUpperCase());
    };

    return keyConverter(obj, upperConverter);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertKeyToLowerCamelCase(obj: Record<string, any>): Record<string, any> {
    const lowerConverter = (key: string): string => {
        return key.replace(/^\S/g, (x) => x.toLowerCase());
    };

    return keyConverter(obj, lowerConverter);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function keyConverter(obj: Record<string, any>, converter: (key: string) => string): Record<string, any> {
    if (typeof obj !== 'object' || obj == null) {
        return obj;
    }

    if (Array.isArray(obj)) {
        const newObj = obj.map((subObj) => keyConverter(subObj, converter));
        return newObj;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newObj: Record<string, any> = {};
        Object.keys(obj).forEach((key) => {
            const newKey: string = converter(key);
            if (typeof obj[key] === 'object') {
                newObj[newKey] = keyConverter(obj[key], converter);
            } else {
                newObj[newKey] = obj[key];
            }
        });
        return newObj;
    }
}

/*
 * Input: string1, string2
 * return 1 if string2 comes before string1 in a list
 * return 0 if string1 and string2 are the same
 * return -1 if string1 comes before string2 in a list
 */
export function compareStrings(string1: string, string2: string): number {
    if (string1 === string2) {
        return 0;
    }
    const string1List = createAlphaNumericList(string1);
    const string2List = createAlphaNumericList(string2);
    const minSize = Math.min(string1List.length, string2List.length);
    for (let i = 0; i < minSize; ++i) {
        if (string1List[i] === string2List[i]) {
            continue;
        }
        const w1: string = string1List[i].toLowerCase();
        const w2: string = string2List[i].toLowerCase();
        if (w1 === w2) {
            return string1List[i] < string2List[i] ? 1 : -1;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w1IsNaN: boolean = isNaN(w1 as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w2IsNaN: boolean = isNaN(w2 as any);
        if (w1IsNaN && w2IsNaN) {
            return w1 > w2 ? 1 : -1;
        } else if (!w1IsNaN && !w2IsNaN) {
            return Number(w1) > Number(w2) ? 1 : -1;
        } else {
            return w1IsNaN ? 1 : -1;
        }
    }
    return string1List.length > string2List.length ? 1 : -1;
}

function createAlphaNumericList(str: string): string[] {
    const regex = /[a-zA-Z]+|[0-9]+|[-_]+/g;
    const alphaNumericList: string[] = [];
    let match = regex.exec(str);
    while (match) {
        alphaNumericList.push(match[0]);
        match = regex.exec(str);
    }
    return alphaNumericList;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function arrayIsNullOrEmpty(array: any[]): boolean {
    return array == null || array.length === 0;
}
